import { useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { CurrencyFormat } from '../currency';
import { NumberFormat, type NumberFormatProps } from '../number';

export interface AssetAmountCash {
  cash?: { currency?: string };
}

export interface AssetAmountEquity {
  equity?: object;
}

export interface AssetAmountMoneyMarket {
  moneyMarket?: object;
}

export type AssetAmountType = AssetAmountCash | AssetAmountEquity | AssetAmountMoneyMarket;

export type AssetAmountDisplay = 'currency' | 'number';

export interface IAssetDisplay {
  type: AssetAmountType;
  decimalPrecision?: number;
  fixedPrecision?: boolean;
}

export type AssetFormatPositiveNegativePrefix = 'auto' | '-l+s' | '+l-s';

export interface AssetFormatProps extends Omit<NumberFormatProps, 'value'> {
  asset: IAssetDisplay | undefined;
  amount: string | number | undefined;
  forceDisplay?: AssetAmountDisplay;
  positiveNegativePrefix?: AssetFormatPositiveNegativePrefix;
}

export function AssetFormat({ asset, amount, forceDisplay, positiveNegativePrefix = 'auto', ...rest }: AssetFormatProps) {
  const fixedPrecision = asset?.decimalPrecision !== undefined && asset?.fixedPrecision ? asset.decimalPrecision : undefined;
  const overrideFormatOptions = useMemo(
    () =>
      match(positiveNegativePrefix)
        .returnType<Partial<NumberFormatProps>>()
        .with(
          '-l+s',
          () =>
            ({
              signDisplay: 'exceptZero',
              minusSignOverride: 'L ',
              plusSignOverride: 'S ',
            }) as const,
        )
        .with(
          '+l-s',
          () =>
            ({
              signDisplay: 'exceptZero',
              minusSignOverride: 'S ',
              plusSignOverride: 'L ',
            }) as const,
        )
        .otherwise(() => ({})),
    [positiveNegativePrefix],
  );

  const display =
    forceDisplay ||
    match(asset)
      .returnType<AssetAmountDisplay>()
      .with({ type: { cash: { currency: P.string } } }, () => 'currency')
      .otherwise(() => 'number');

  const currency = match(asset)
    .with({ type: { cash: { currency: P.string } } }, (a) => a.type.cash.currency)
    .otherwise(() => 'USD');

  if (display === 'currency') {
    return (
      <CurrencyFormat {...rest} currency={currency} maximumFractionDigits={fixedPrecision} minimumFractionDigits={fixedPrecision} value={amount} />
    );
  }

  return (
    <NumberFormat value={amount} {...rest} {...overrideFormatOptions} maximumFractionDigits={fixedPrecision} minimumFractionDigits={fixedPrecision} />
  );
}
